import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Sheet } from 'react-modal-sheet'
import { Avatar, AvatarWithoutDot } from '@components/Avatar/Avatar'
import { MutualTowns } from '@components/MutualTowns/MutualTowns'
import { Panel } from '@components/Panel/Panel'
import { TableCell } from '@components/TableCell/TableCell'
import { TipCounter } from '@components/TipCounter/TipCounter'
import { UserRankBadge } from '@components/UserRankBadge/UserRankBadge'
import { UserRolePills } from '@components/UserRoles/UserRoles'
import { LIVE_CONSTANTS } from '@harmony/live-types'
import { Box, Button, CardOpener, Dropdown, Icon, Paragraph, Stack, Text } from '@ui'
import { useMobile } from 'hooks/useMobile'
import { useGetUserBio } from 'hooks/useUserBio'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useCardOpenerContext } from 'ui/components/Overlay/CardOpenerContext'
import { modalSheetClass } from 'ui/styles/globals/sheet.css'
import { useMyUserId, useSpaceId, useUserLookupArray, useUserLookupContext } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { useLiveAudioState, useLiveChannelState, useLiveClient } from './LiveClientProvider'
import { SpeakingAvatar } from './SpeakingAvatar'

// Generate stage size options (1 to MAX_STAGE_SIZE)
const stageSizeOptions = Array.from({ length: LIVE_CONSTANTS.MAX_STAGE_SIZE }, (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
}))

type ParticipantInfo = {
    userId: string
    name: string
    section: 'request' | 'stage' | 'audience'
}

export interface LiveParticipantsPanelProps {
    spaceId: string
    channelId: string
    channelName?: string
}

export const LiveParticipantsPanel = React.memo(function LiveParticipantsPanel(
    props: LiveParticipantsPanelProps,
) {
    const { spaceId, channelId, channelName } = props
    const channelState = useLiveChannelState(spaceId, channelId)
    const audioState = useLiveAudioState()
    const { moderateStage } = useLiveClient()
    const currentUserId = useMyUserId()
    const isMobile = useMobile()

    // Mobile sheet state - tracks which user's profile sheet is open
    const [profileSheetUserId, setProfileSheetUserId] = useState<string | null>(null)

    // Check if current user is on stage
    const isOnStage = useMemo(() => {
        if (!channelState || !currentUserId) {
            return false
        }
        return channelState.stage.userIds.some((id) => id === currentUserId)
    }, [channelState, currentUserId])

    // Get all user IDs for lookup
    const allUserIds = useMemo(() => {
        if (!channelState) {
            return []
        }
        return [
            ...channelState.stageRequests.userIds,
            ...channelState.stage.userIds,
            ...channelState.listeners.userIds,
        ]
    }, [channelState])

    const lookupUsers = useUserLookupArray(allUserIds, spaceId)

    // Build participant list
    const participants = useMemo((): ParticipantInfo[] => {
        if (!channelState) {
            return []
        }

        const result: ParticipantInfo[] = []

        // Add stage requests (shown in the "Requests" section for moderators)
        for (const userId of channelState.stageRequests.userIds) {
            const user = lookupUsers.find((u) => u.userId === userId)
            result.push({
                userId,
                name: user ? getPrettyDisplayName(user) : userId.slice(0, 8),
                section: 'request',
            })
        }

        // Add stage users
        for (const userId of channelState.stage.userIds) {
            const user = lookupUsers.find((u) => u.userId === userId)
            result.push({
                userId,
                name: user ? getPrettyDisplayName(user) : userId.slice(0, 8),
                section: 'stage',
            })
        }

        // Add audience (listeners + stage requesters)
        // Users requesting stage should remain in the audience until they're actually on stage
        // This preserves the total participant count (stage + audience)
        const audienceUserIds = new Set(channelState.listeners.userIds)
        // Include stage requesters in audience - they haven't left yet
        for (const userId of channelState.stageRequests.userIds) {
            audienceUserIds.add(userId)
        }

        for (const userId of audienceUserIds) {
            const user = lookupUsers.find((u) => u.userId === userId)
            result.push({
                userId,
                name: user ? getPrettyDisplayName(user) : userId.slice(0, 8),
                section: 'audience',
            })
        }

        return result
    }, [channelState, lookupUsers])

    // Split participants by section
    const groups = useMemo(() => {
        const requests: ParticipantInfo[] = []
        const stage: ParticipantInfo[] = []
        const audience: ParticipantInfo[] = []

        for (const participant of participants) {
            switch (participant.section) {
                case 'request':
                    requests.push(participant)
                    break
                case 'stage':
                    stage.push(participant)
                    break
                case 'audience':
                    audience.push(participant)
                    break
            }
        }

        return { requests, stage, audience }
    }, [participants])

    // Calculate visible item count - non-stage users can't see requests
    const visibleItemCount = useMemo(() => {
        if (isOnStage) {
            return participants.length
        }
        // Non-stage users can't see the requests section
        return groups.stage.length + groups.audience.length
    }, [isOnStage, participants.length, groups.stage.length, groups.audience.length])

    const handleAccept = useCallback(
        (targetUserId: string) => {
            moderateStage(channelId, { type: 'accept', targetUserId })
        },
        [channelId, moderateStage],
    )

    const handleDeny = useCallback(
        (targetUserId: string) => {
            moderateStage(channelId, { type: 'deny', targetUserId })
        },
        [channelId, moderateStage],
    )

    const handleSetStageSize = useCallback(
        (value: string) => {
            const stageSize = parseInt(value, 10)
            if (stageSize >= 1 && stageSize <= LIVE_CONSTANTS.MAX_STAGE_SIZE) {
                moderateStage(channelId, { type: 'set_stage_size', stageSize })
            }
        },
        [channelId, moderateStage],
    )

    const handleKick = useCallback(
        (targetUserId: string) => {
            moderateStage(channelId, { type: 'kick', targetUserId })
        },
        [channelId, moderateStage],
    )

    // Mobile sheet handlers
    const openProfileSheet = useCallback((userId: string) => {
        setProfileSheetUserId(userId)
    }, [])

    const closeProfileSheet = useCallback(() => {
        setProfileSheetUserId(null)
    }, [])

    // Check if we can kick the user in the sheet (must be on stage, viewing a stage user who isn't us)
    const profileSheetCanKick = useMemo(() => {
        if (!profileSheetUserId || !isOnStage || profileSheetUserId === currentUserId) {
            return false
        }
        return channelState?.stage.userIds.includes(profileSheetUserId) ?? false
    }, [profileSheetUserId, isOnStage, currentUserId, channelState?.stage.userIds])

    const handleKickFromSheet = useCallback(() => {
        if (profileSheetUserId) {
            handleKick(profileSheetUserId)
            closeProfileSheet()
        }
    }, [profileSheetUserId, handleKick, closeProfileSheet])

    const stageSize = channelState?.stageSize ?? LIVE_CONSTANTS.DEFAULT_STAGE_SIZE

    const { openPanel } = usePanelActions()

    const onOpenProfile = useCallback(
        (profileId: string) => {
            openPanel('profile', { profileId })
        },
        [openPanel],
    )

    const handleViewProfileFromSheet = useCallback(() => {
        if (profileSheetUserId) {
            onOpenProfile(profileSheetUserId)
            closeProfileSheet()
        }
    }, [profileSheetUserId, onOpenProfile, closeProfileSheet])

    const panelLabel = channelName ? `Live: ${channelName}` : 'Live Participants'

    return (
        <>
            <Panel label={panelLabel} padding="none">
                <Stack padding minHeight="forceScroll" gap="md">
                    <Box grow gap>
                        {/* Stage Requests section - only show to users on stage */}
                        {isOnStage && groups.requests.length > 0 && (
                            <>
                                <Stack gap="md">
                                    <Stack horizontal gap="sm" alignItems="center">
                                        <Icon type="blockHand" size="square_sm" color="gray2" />
                                        <Paragraph color="gray2">Requests</Paragraph>
                                    </Stack>
                                    <Stack gap="sm">
                                        {groups.requests.map((participant) => (
                                            <StageRequestRow
                                                key={participant.userId}
                                                userId={participant.userId}
                                                name={participant.name}
                                                onAccept={() => handleAccept(participant.userId)}
                                                onDeny={() => handleDeny(participant.userId)}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                                <Box style={{ height: 1 }} background="level2" />
                            </>
                        )}

                        {/* Stage section */}
                        {groups.stage.length > 0 && (
                            <>
                                <Stack gap="md">
                                    <Stack
                                        horizontal
                                        gap="sm"
                                        alignItems="center"
                                        justifyContent="spaceBetween"
                                    >
                                        <Stack horizontal gap="sm" alignItems="center">
                                            <Icon
                                                type="microphone"
                                                size="square_sm"
                                                color="gray2"
                                            />
                                            <Paragraph color="gray2">Stage</Paragraph>
                                        </Stack>
                                        {isOnStage && (
                                            <CardOpener
                                                placement="dropdown"
                                                render={
                                                    <Box paddingY="md">
                                                        <StageSizeDropdown
                                                            value={stageSize}
                                                            onChange={handleSetStageSize}
                                                        />
                                                    </Box>
                                                }
                                            >
                                                {({ triggerProps }) => (
                                                    <Stack
                                                        horizontal
                                                        hoverable
                                                        rounded="full"
                                                        paddingX="x1.5"
                                                        paddingY="xs"
                                                        background="level2"
                                                        gap="xs"
                                                        alignItems="center"
                                                        {...triggerProps}
                                                    >
                                                        <Text size="sm" color="gray2">
                                                            {stageSize} Max
                                                        </Text>
                                                        <Icon
                                                            type="more"
                                                            size="square_xs"
                                                            color="gray2"
                                                        />
                                                    </Stack>
                                                )}
                                            </CardOpener>
                                        )}
                                    </Stack>
                                    <Box
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns:
                                                'repeat(auto-fill, minmax(100px, 1fr))',
                                            columnGap: 'var(--space-sm)',
                                            rowGap: 'var(--space-md)',
                                        }}
                                    >
                                        {groups.stage.map((participant) => (
                                            <ParticipantGridItem
                                                showSpeakingIndicator
                                                key={participant.userId}
                                                userId={participant.userId}
                                                name={participant.name}
                                                isSpeaking={
                                                    audioState?.speakingUsers?.has(
                                                        participant.userId,
                                                    ) ?? false
                                                }
                                                canKick={
                                                    isOnStage &&
                                                    participant.userId !== currentUserId
                                                }
                                                isMobile={isMobile}
                                                onClick={() => onOpenProfile(participant.userId)}
                                                onKick={handleKick}
                                                onOpenKickSheet={openProfileSheet}
                                            />
                                        ))}
                                    </Box>
                                </Stack>
                                {groups.audience.length > 0 && (
                                    <Box style={{ height: 1 }} background="level2" />
                                )}
                            </>
                        )}

                        {/* Audience section */}
                        {groups.audience.length > 0 && (
                            <Stack gap="md">
                                <Stack horizontal gap="sm" alignItems="center">
                                    <Icon type="people" size="square_sm" color="gray2" />
                                    <Paragraph color="gray2">Audience</Paragraph>
                                </Stack>
                                <Box
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(100px, 1fr))',
                                        columnGap: 'var(--space-sm)',
                                        rowGap: 'var(--space-md)',
                                    }}
                                >
                                    {groups.audience.map((participant) => (
                                        <ParticipantGridItem
                                            key={participant.userId}
                                            userId={participant.userId}
                                            name={participant.name}
                                            isSpeaking={false}
                                            showSpeakingIndicator={false}
                                            isMobile={isMobile}
                                            onClick={() => onOpenProfile(participant.userId)}
                                            onOpenKickSheet={openProfileSheet}
                                        />
                                    ))}
                                </Box>
                            </Stack>
                        )}

                        {/* Empty state */}
                        {visibleItemCount === 0 && (
                            <Box
                                padding
                                horizontal
                                gap="sm"
                                background="level2"
                                height="x7"
                                alignItems="center"
                                rounded="sm"
                                color="gray2"
                            >
                                <Icon type="people" size="square_xs" />
                                <Paragraph>No participants yet</Paragraph>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Panel>

            {/* Mobile profile sheet */}
            <Sheet
                className={modalSheetClass}
                isOpen={profileSheetUserId !== null}
                detent="content-height"
                onClose={closeProfileSheet}
            >
                <Sheet.Container>
                    <Sheet.Header />
                    <Sheet.Content>
                        <Sheet.Scroller>
                            <Stack paddingX gap="sm" paddingBottom="lg">
                                {/* Profile card content */}
                                {profileSheetUserId && (
                                    <ProfileCardContent userId={profileSheetUserId} />
                                )}

                                {/* Divider */}
                                <Box style={{ height: 1 }} background="level2" />

                                <Stack>
                                    {/* Action menu */}
                                    <TableCell
                                        iconType="people"
                                        text="View Profile"
                                        onClick={handleViewProfileFromSheet}
                                    />
                                    {profileSheetCanKick && (
                                        <TableCell
                                            isError
                                            iconType="logout"
                                            text="Kick User"
                                            onClick={handleKickFromSheet}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </Sheet.Scroller>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop onTap={closeProfileSheet} />
            </Sheet>
        </>
    )
})

interface StageRequestRowProps {
    userId: string
    name: string
    onAccept: () => void
    onDeny: () => void
}

const StageRequestRow = React.memo(function StageRequestRow(props: StageRequestRowProps) {
    const { userId, name, onAccept, onDeny } = props

    return (
        <Stack
            horizontal
            alignItems="center"
            background="level2"
            borderRadius="sm"
            padding="sm"
            gap="sm"
        >
            <Avatar size="avatar_md" userId={userId} />
            <Stack grow gap="paragraph">
                <Text truncate fontWeight="medium">
                    {name}
                </Text>
                <Text size="sm" color="positive">
                    Requests Stage
                </Text>
            </Stack>
            <Stack horizontal gap="xs">
                <Button size="button_sm" tone="positive" onClick={onAccept}>
                    Allow
                </Button>
                <Button size="button_sm" tone="level3" onClick={onDeny}>
                    Ignore
                </Button>
            </Stack>
        </Stack>
    )
})

interface ParticipantGridItemProps {
    userId: string
    name: string
    isSpeaking: boolean
    showSpeakingIndicator: boolean
    onClick: () => void
    canKick?: boolean
    isMobile?: boolean
    onKick?: (userId: string) => void
    onOpenKickSheet?: (userId: string) => void
}

const ParticipantGridItem = React.memo(function ParticipantGridItem(
    props: ParticipantGridItemProps,
) {
    const {
        userId,
        name,
        isSpeaking,
        showSpeakingIndicator,
        onClick,
        canKick,
        isMobile,
        onKick,
        onOpenKickSheet,
    } = props

    const avatarRef = useRef<HTMLDivElement>(null!)

    // Avatar component
    const avatar = (
        <Box ref={avatarRef}>
            {showSpeakingIndicator ? (
                <SpeakingAvatar userId={userId} isSpeaking={isSpeaking} size="avatar_md" />
            ) : (
                <AvatarWithoutDot size="avatar_md" userId={userId} />
            )}
        </Box>
    )

    // Name row
    const nameRow = (
        <Text truncate size="sm" textAlign="center" fontWeight="medium" maxWidth="x10">
            {name}
        </Text>
    )

    // On mobile: click opens sheet with profile card
    if (isMobile) {
        return (
            <Stack
                centerContent
                cursor="pointer"
                gap="sm"
                data-testid={`participant-${userId}`}
                onClick={() => onOpenKickSheet?.(userId)}
            >
                {avatar}
                {nameRow}
            </Stack>
        )
    }

    // On desktop: CardOpener with overrideTriggerRef pointing to avatar
    return (
        <CardOpener
            placement="dropdown"
            horizontalAlignment="center"
            overrideTriggerRef={avatarRef}
            render={
                <Box padding="xs">
                    <ParticipantMenu
                        userId={userId}
                        canKick={canKick}
                        onKick={onKick}
                        onViewProfile={onClick}
                    />
                </Box>
            }
        >
            {({ triggerProps }) => (
                <Stack
                    {...triggerProps}
                    centerContent
                    hoverable
                    rounded
                    background="level1"
                    cursor="pointer"
                    gap="sm"
                    paddingY="sm"
                    data-testid={`participant-${userId}`}
                >
                    {avatar}
                    {nameRow}
                </Stack>
            )}
        </CardOpener>
    )
})

interface ProfileCardContentProps {
    userId: string
}

const ProfileCardContent = React.memo(function ProfileCardContent(props: ProfileCardContentProps) {
    const { userId } = props
    const { lookupUser } = useUserLookupContext()
    const user = lookupUser(userId)
    const { data: userBio } = useGetUserBio(userId)
    const spaceId = useSpaceId()

    if (!user) {
        return null
    }

    const ensName = user.ensName
    const displayName = ensName || user.displayName || user.username || ''
    const username = user.username || ''
    const userRoles = spaceId ? <UserRolePills userId={userId} spaceId={spaceId} /> : null

    return (
        <Stack gap padding="sm">
            <Stack horizontal gap>
                <Avatar userId={userId} size="avatar_lg" />
                <Stack justifyContent="center" gap="sm">
                    {!!displayName && (
                        <Stack horizontal gap="xs" alignItems="center" height="x1.5">
                            <Paragraph truncate fontWeight="strong" color="default">
                                {displayName}
                            </Paragraph>
                            {user.isBot && <Icon type="robot" size="square_xs" color="gray2" />}
                            <TipCounter userId={user.userId} />
                        </Stack>
                    )}
                    {!!username && (
                        <Paragraph truncate color="gray2">
                            @{username}
                        </Paragraph>
                    )}
                </Stack>
            </Stack>
            <Stack horizontal gap="xs" color="gray1" alignItems="center" paddingX="sm">
                <MutualTowns userId={user.userId} />
            </Stack>
            {userBio && (
                <Stack gap="sm">
                    <Paragraph fontWeight="strong" size="md">
                        Bio
                    </Paragraph>
                    <Paragraph size="md" color="gray2">
                        {userBio}
                    </Paragraph>
                </Stack>
            )}
            {userRoles && (
                <Stack gap="sm">
                    <Paragraph fontWeight="strong">Roles</Paragraph>
                    <Stack>{userRoles}</Stack>
                </Stack>
            )}
            <UserRankBadge targetUserId={userId} />
        </Stack>
    )
})

// Mini component for action menu items
type ActionMenuItemProps = {
    icon: React.ComponentProps<typeof Icon>['type']
    label: string
    isError?: boolean
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

const ActionMenuItem = ({ icon, label, isError, onClick }: ActionMenuItemProps) => (
    <Stack
        horizontal
        hoverable
        elevate
        cursor="pointer"
        gap="sm"
        alignItems="center"
        padding="xs"
        borderRadius="sm"
        color={isError ? 'error' : undefined}
        onClick={onClick}
    >
        <Icon
            type={icon}
            size="square_lg"
            color={isError ? 'error' : 'gray2'}
            background="level2"
            padding="sm"
        />
        <Text noWrap color={isError ? 'error' : undefined}>
            {label}
        </Text>
    </Stack>
)

interface ParticipantMenuProps {
    userId: string
    canKick?: boolean
    onKick?: (userId: string) => void
    onViewProfile?: () => void
}

const ParticipantMenu = React.memo(function ParticipantMenu(props: ParticipantMenuProps) {
    const { userId, canKick, onKick, onViewProfile } = props
    const { closeCard } = useCardOpenerContext()

    const handleViewProfile = useCallback(() => {
        onViewProfile?.()
        closeCard()
    }, [onViewProfile, closeCard])

    const handleKick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation()
            e.preventDefault()
            onKick?.(userId)
            closeCard()
        },
        [userId, onKick, closeCard],
    )

    return (
        <Box
            border
            padding="md"
            background="level2"
            rounded="md"
            boxShadow="dropdown"
            minWidth="250"
        >
            <Stack gap="sm">
                <ProfileCardContent userId={userId} />
                <Box style={{ height: 1 }} background="level3" />
                <Stack gap="xs" insetX="xxs">
                    <ActionMenuItem
                        icon="people"
                        label="View Profile"
                        onClick={handleViewProfile}
                    />
                    {canKick && (
                        <ActionMenuItem
                            isError
                            icon="logout"
                            label="Kick User"
                            onClick={handleKick}
                        />
                    )}
                </Stack>
            </Stack>
        </Box>
    )
})

interface StageSizeDropdownProps {
    value: number
    onChange: (value: string) => void
}

const StageSizeDropdown = React.memo(function StageSizeDropdown(props: StageSizeDropdownProps) {
    const { value } = props
    const { closeCard } = useCardOpenerContext()

    const onChange = useCallback(
        (value: string) => {
            closeCard()
            props.onChange(value)
        },
        [props, closeCard],
    )

    return (
        <Box border padding="md" background="level2" rounded="md" boxShadow="dropdown">
            <Stack gap width="150">
                <Text fontWeight="medium" whiteSpace="nowrap">
                    Stage Size
                </Text>
                <Dropdown
                    background="level3"
                    height="x3.5"
                    width="100%"
                    rounded="full"
                    value={String(value)}
                    options={stageSizeOptions}
                    onChange={onChange}
                />
            </Stack>
        </Box>
    )
})
